import React, { useState, useEffect } from 'react';
import defaultAvatar from '../../../../pic/avatar.png';
import { fetchAvatar } from '../../../../services/userService'; // No need to fetch profile if selectedUser is passed
import { getGarminConnectLink } from '../../../../services/garminService';
import './PersonalInfo.css';
import CircularProgress from '@mui/material/CircularProgress';
import { handleSessionExpired } from '../../../SessionExpiredHandler';
import SessionExpiredModal from '../../../SessionExpiredModal';
import { useTranslation } from 'react-i18next';


const PersonalInfo = ({ user, onUpdate, editable = true }) => {
    const [editMode, setEditMode] = useState(false);
    const [avatar, setAvatar] = useState(defaultAvatar);
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        avatar: '',
        username: '',
        place: '',
        street: '',
        telephone: ''
    });
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [connectingGarmin, setConnectingGarmin] = useState(false);
    //   console.log("personal info selected user: ", user)

    useEffect(() => {
        const initializeData = async () => {
            try {
                if (user) {
                    // Use the passed user data directly
                    setFormData({
                        firstName: user.firstname || '',
                        lastName: user.lastname || '',
                        email: user.email || '',
                        avatar: user.avatar || '',
                        username: user.username || '',
                        place: user.place || '',
                        street: user.street || '',
                        telephone: user.telephone || ''
                    });

                    // Load avatar
                    await loadAvatar(user._id);
                }
            } catch (error) {
                console.error('Error initializing user data:', error);
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, [user]);


    const loadAvatar = async (userId) => {
        try {
            const base64Image = await fetchAvatar(userId);
            if (base64Image) {
                setAvatar(`data:image/jpeg;base64,${base64Image}`);
            } else {
                setAvatar(defaultAvatar);
            }
        } catch (error) {
            console.error('Failed to load avatar:', error);
            setAvatar(defaultAvatar);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                avatar: file,
            }));

            // Show preview of selected image
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatar(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const updateFormData = new FormData();
        Object.keys(formData).forEach((key) => {
            updateFormData.append(key, formData[key]);
        });

        try {
            await onUpdate(updateFormData); // Pass FormData to onUpdate
            alert('Profile updated successfully!');
            setEditMode(false); // Exit edit mode after updating

            // Reload avatar after successful update
            if (user && user._id) {
                await loadAvatar(user._id);
            }
        } catch (error) {
            console.error('Failed to update profile:', error);
            alert('Failed to update profile. Please check the console for more information.');
        }
    };

    // Open the user's personal Garmin authorization page in a new tab. After they log in
    // and authorize, their Garmin data flows to Fitrockr and updates on the dashboard.
    const handleConnectGarmin = async () => {
        try {
            setConnectingGarmin(true);
            const url = await getGarminConnectLink();
            window.open(url, '_blank', 'noopener');
        } catch (error) {
            console.error('Garmin connect failed:', error);
            alert(t('personalInfo.garminConnectError', 'Could not start Garmin connection. Please try again.'));
        } finally {
            setConnectingGarmin(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-indicator">
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="personal-info">
            {editMode && editable ? (
                <form onSubmit={handleSubmit} className="personal-info-form">
                    <div className="avatar-section">
                        <img src={avatar} alt="Avatar" className="avatar-preview" />
                        <label className="form-label">
                            {t('personalInfo.uploadNewAvatar', 'Upload New Avatar')}:
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept="image/*"
                                className="form-input"
                                disabled={!editable}
                            />
                        </label>
                    </div>
                    <label className="form-label">
                        {t('personalInfo.firstName')}:
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName || ''}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={!editable}
                        />
                    </label>
                    <label className="form-label">
                        {t('personalInfo.lastName')}:
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName || ''}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={!editable}
                        />
                    </label>
                    <label className="form-label">
                        {t('personalInfo.place')}:
                        <input
                            type="text"
                            name="place"
                            value={formData.place || ''}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={!editable}
                        />
                    </label>
                    <label className="form-label">
                        {t('personalInfo.street')}:
                        <input
                            type="text"
                            name="street"
                            value={formData.street || ''}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={!editable}
                        />
                    </label>
                    <label className="form-label">
                        {t('personalInfo.telephone')}:
                        <input
                            type="text"
                            name="telephone"
                            value={formData.telephone || ''}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={!editable}
                        />
                    </label>
                    <label className="form-label">
                        {t('personalInfo.email')}:
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ''}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={!editable}
                        />
                    </label>
                    {editable && <button type="submit" className="submit-button">{t('personalInfo.save')}</button>}
                </form>
            ) : (
                <div className="personal-info-display">
                    <div className="avatar-display-section">
                        <img src={avatar} alt="Avatar" className="avatar" />
                        {avatar === defaultAvatar && editable && (
                            <p className="avatar-hint">{t('personalInfo.avatarHint', 'Click Edit to upload your avatar')}</p>
                        )}
                    </div>
                    <p className="info-text">
                        <strong>{t('personalInfo.username')}:</strong> {formData.username || t('personalInfo.notProvided')}
                    </p>
                    <p className="info-text">
                        <strong>{t('personalInfo.firstName')}:</strong> {formData.firstName || t('personalInfo.notProvided')}
                    </p>
                    <p className="info-text">
                        <strong>{t('personalInfo.lastName')}:</strong> {formData.lastName || t('personalInfo.notProvided')}
                    </p>
                    <p className="info-text">
                        <strong>{t('personalInfo.email')}:</strong> {formData.email || t('personalInfo.notProvided')}
                    </p>
                    <p className="info-text">
                        <strong>{t('personalInfo.place')}:</strong> {formData.place || t('personalInfo.notProvided')}
                    </p>
                    <p className="info-text">
                        <strong>{t('personalInfo.street')}:</strong> {formData.street || t('personalInfo.notProvided')}
                    </p>
                    <p className="info-text">
                        <strong>{t('personalInfo.telephone')}:</strong> {formData.telephone || t('personalInfo.notProvided')}
                    </p>
                    {editable && (
                        <div className="garmin-connect-section">
                            <button
                                type="button"
                                onClick={handleConnectGarmin}
                                className="garmin-connect-button"
                                disabled={connectingGarmin}
                            >
                                {connectingGarmin
                                    ? t('personalInfo.connecting', 'Connecting…')
                                    : t('personalInfo.connectGarmin', 'Connect Garmin')}
                            </button>
                            <p className="garmin-connect-hint">
                                {t('personalInfo.connectGarminHint', 'Log in to your Garmin account to sync your health data. After connecting, use the Refresh button on Body Metrics to update your dashboard.')}
                            </p>
                        </div>
                    )}
                    {editable && <button onClick={() => setEditMode(true)} className="edit-button">{t('personalInfo.edit')}</button>}
                </div>
            )}
            <SessionExpiredModal open={modalOpen} handleClose={() => setModalOpen(false)} />
        </div>
    );
};

export default PersonalInfo;